import { useState, useEffect, useCallback, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { MainLayout } from '../../components/MainLayout';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card } from '../../components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '../../components/ui/sheet';
import { Skeleton } from '../../components/ui/skeleton';
import {
  ShieldCheck, Play, RefreshCw, Download, Loader2, CheckCircle2,
  XCircle, AlertTriangle, Info, FileText, ChevronRight, ChevronDown,
  Wrench,
} from 'lucide-react';

const fmtC = (n) =>
  n == null ? '\u2014' : `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (v) => {
  if (!v) return '\u2014';
  const d = new Date(v);
  return isNaN(d) ? v : d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' });
};

const STATUS_META = {
  passed: { label: 'Pass', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200', Icon: CheckCircle2 },
  failed: { label: 'Fail', cls: 'bg-red-100 text-red-700 border-red-200', Icon: XCircle },
  partial: { label: 'Partial', cls: 'bg-amber-100 text-amber-700 border-amber-200', Icon: AlertTriangle },
  skipped: { label: 'Skipped', cls: 'bg-muted text-muted-foreground', Icon: Info },
  error: { label: 'Error', cls: 'bg-red-100 text-red-700 border-red-200', Icon: XCircle },
};

const RULE_LABELS = {
  new_charges_equals_total: 'New charges / totals reconcile',
  duplicate_check: 'Duplicate check',
  auto_assign_property: 'Auto-assign property',
  _parsed_data: 'Parsed data present',
};

// Human-readable explanation for why a validation-passed bill is NOT promoted.
const PROMOTION_REASON_LABELS = {
  approval_pending: 'Awaiting approval — approve this bill to promote it',
  onboarding_incomplete: 'Onboarding data incomplete for this account',
  validation_failed: 'Validation did not reconcile',
  include_off: 'Parsed bills are excluded from All Bills (org setting)',
  unparsed: 'Bill has not been parsed yet',
};
const promotionReasonLabel = (reason) =>
  reason ? (PROMOTION_REASON_LABELS[reason] || reason.replace(/_/g, ' ')) : 'Not yet promoted';

function StatusBadge({ status }) {
  const m = STATUS_META[status] || { label: status, cls: 'bg-muted text-muted-foreground', Icon: Info };
  const { Icon } = m;
  return (
    <Badge variant="outline" className={`gap-1 ${m.cls}`} data-testid={`validation-status-${status}`}>
      <Icon className="h-3 w-3" /> {m.label}
    </Badge>
  );
}

function ReasonBadges({ reasons }) {
  if (!reasons || reasons.length === 0) return <span className="text-muted-foreground">\u2014</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {reasons.map((r) => (
        <Badge key={r.code} variant="outline" className="bg-red-50 text-red-700 border-red-200" data-testid={`reason-${r.code}`}>
          {r.label}
        </Badge>
      ))}
    </div>
  );
}

function RuleDetailRow({ row }) {
  const results = (row.results || []).filter((r) => r.rule_key !== '_parsed_data' || r.status === 'failed');
  return (
    <div className="bg-muted/40 px-6 py-4 space-y-2" data-testid={`validation-accordion-${row.bill_id}`}>
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Rule results</div>
      <div className="grid gap-2">
        {results.map((r) => (
          <div key={r.rule_key} className="flex items-start gap-3 text-sm">
            <StatusBadge status={r.status} />
            <div className="flex-1">
              <div className="font-medium">{RULE_LABELS[r.rule_key] || r.rule_key}</div>
              {r.status !== 'passed' && r.detail && (
                <pre className="mt-1 text-xs text-muted-foreground whitespace-pre-wrap break-all">
                  {JSON.stringify(r.detail, null, 2)}
                </pre>
              )}
            </div>
          </div>
        ))}
      </div>
      {!row.promoted && (
        <div
          className="mt-2 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800"
          data-testid={`validation-not-promoted-reason-${row.bill_id}`}
        >
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <div>
            <span className="font-medium">Not promoted to All Bills. </span>
            {promotionReasonLabel(row.promotion_blocked_reason)}
            {row.promotion_blocked_reason === 'approval_pending'
              && ' — open the bill and approve it to promote.'}
          </div>
        </div>
      )}
    </div>
  );
}

export default function ValidationReport() {
  const [currentAccount] = useState(() => {
    const s = localStorage.getItem('current_account');
    return s ? JSON.parse(s) : null;
  });
  const accountId = currentAccount?.id;
  const navigate = useNavigate();

  const [report, setReport] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [running, setRunning] = useState(false);
  const [tab, setTab] = useState('failed');
  const [activeRule, setActiveRule] = useState(null);
  const [expanded, setExpanded] = useState(null);

  // Bill PDF drawer
  const [billOpen, setBillOpen] = useState(false);
  const [billRow, setBillRow] = useState(null);
  const [billPdf, setBillPdf] = useState(null);
  const [billUrl, setBillUrl] = useState(null);
  const [billLoading, setBillLoading] = useState(false);

  const PAGE_SIZE = 50;

  const listParams = (offset, rule) => ({
    account_id: accountId,
    status_group: tab,
    rule_key: rule !== undefined ? rule : (activeRule || undefined),
    limit: PAGE_SIZE,
    offset,
  });

  const loadReport = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    try {
      const [{ data: rep }, { data: list }] = await Promise.all([
        api.get('/intake/validation/report', { params: { account_id: accountId } }),
        api.get('/intake/validation/list', {
          params: { account_id: accountId, status_group: tab, limit: PAGE_SIZE, offset: 0 },
        }),
      ]);
      setReport(rep);
      setRows(list.items || []);
      setTotal(list.total || 0);
      setActiveRule(null);
      setExpanded(null);
    } catch (e) {
      toast.error('Failed to load validation report');
    } finally {
      setLoading(false);
    }
  }, [accountId, tab]);

  useEffect(() => { loadReport(); }, [loadReport]);

  const filterByRule = async (ruleKey) => {
    setActiveRule(ruleKey);
    setExpanded(null);
    try {
      const { data } = await api.get('/intake/validation/list', { params: listParams(0, ruleKey) });
      setRows(data.items || []);
      setTotal(data.total || 0);
    } catch {
      toast.error('Failed to filter');
    }
  };

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const { data } = await api.get('/intake/validation/list', { params: listParams(rows.length) });
      setRows((prev) => [...prev, ...(data.items || [])]);
      setTotal(data.total || 0);
    } catch {
      toast.error('Failed to load more');
    } finally {
      setLoadingMore(false);
    }
  };

  const runEngine = async () => {
    setRunning(true);
    try {
      const { data } = await api.post('/intake/validation/run', { limit: 300 },
        { params: { account_id: accountId } });
      if (data.skipped_no_config) {
        toast.info('No workflow config for this org — engine skipped (legacy flow preserved).');
      } else {
        toast.success(`Validated ${data.ran} bills · ${data.passed} pass · ${data.failed} fail · ${data.promoted} promoted`);
      }
      await loadReport();
    } catch {
      toast.error('Failed to run validation');
    } finally {
      setRunning(false);
    }
  };

  const downloadCsv = async () => {
    try {
      const { data } = await api.get('/intake/validation/export.csv', {
        params: { account_id: accountId, rule_key: activeRule || undefined },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement('a');
      a.href = url; a.download = `validation_${tab}.csv`; a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Download failed');
    }
  };

  const openBill = async (row) => {
    setBillRow(row); setBillOpen(true); setBillPdf(null); setBillUrl(null); setBillLoading(true);
    try {
      const { data } = await api.get(`/payments/v2/bill-pdf/${row.bill_id}`,
        { params: { account_id: accountId } });
      if (data?.base64_data) setBillPdf(data.base64_data);
      else if (data?.pdf_url) setBillUrl(data.pdf_url);
    } catch {
      setBillPdf(null); setBillUrl(null);
    } finally { setBillLoading(false); }
  };

  const reviewFix = (row) => {
    // PaaS parsed bills live in the `documents` collection (validated via the
    // schedule engine) — open the document viewer. Legacy synced bills open the
    // bill detail page.
    if (row.source === 'paas_schedule') {
      navigate(`/viewer/${row.bill_id}?account_id=${accountId}`);
    } else {
      navigate(`/bills/${row.bill_id}?account_id=${accountId}&tab=validations`);
    }
  };

  const billPdfUrl = billPdf ? `data:application/pdf;base64,${billPdf}` : (billUrl || null);
  const isFailedTab = tab === 'failed';

  const tile = (t) => (
    <Card key={t.window} className="p-5" data-testid={`validation-report-tile-${t.window}`}>
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{t.window} window</div>
      <div className="mt-1 text-3xl font-semibold">{t.total}</div>
      <div className="mt-3 flex gap-3 text-sm">
        <span className="text-emerald-600">{t.passed} pass</span>
        <span className="text-red-600">{t.failed} fail</span>
        <span className="text-amber-600">{t.partial} partial</span>
      </div>
    </Card>
  );

  const colCount = isFailedTab ? 8 : 7;

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="validation-report-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-semibold flex items-center gap-2">
              <ShieldCheck className="h-6 w-6 text-primary" /> Intake Validation Report
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Results of the workflow-config validation rules run against parsed bills.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={loadReport} data-testid="validation-refresh-btn">
              <RefreshCw className="h-4 w-4 mr-1" /> Refresh
            </Button>
            <Button variant="outline" size="sm" onClick={downloadCsv} data-testid="validation-csv-btn">
              <Download className="h-4 w-4 mr-1" /> CSV
            </Button>
            <Button size="sm" onClick={runEngine} disabled={running} data-testid="validation-run-btn">
              {running ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Play className="h-4 w-4 mr-1" />}
              Run Validation
            </Button>
          </div>
        </div>

        {!loading && report && !report.has_config && (
          <Card className="p-4 border-amber-200 bg-amber-50 flex items-start gap-2" data-testid="validation-no-config">
            <Info className="h-5 w-5 text-amber-600 mt-0.5" />
            <div className="text-sm text-amber-800">
              This org has no Workflow Config — the validation engine is intentionally disabled and today's
              intake flow is preserved. A Super Admin can enable it under <strong>Edit Org → Workflow Config</strong>.
            </div>
          </Card>
        )}

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => <Skeleton key={i} className="h-28" />)}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {(report?.tiles || []).map(tile)}
          </div>
        )}

        <Tabs value={tab} onValueChange={(v) => { setTab(v); }}>
          <TabsList data-testid="validation-tabs">
            <TabsTrigger value="failed" data-testid="validation-tab-failed">Validation Failed</TabsTrigger>
            <TabsTrigger value="passed" data-testid="validation-tab-passed">Validation Passed</TabsTrigger>
          </TabsList>
        </Tabs>

        {isFailedTab && report?.failure_groups?.length > 0 && (
          <Card className="p-4" data-testid="validation-failure-groups">
            <div className="text-sm font-medium mb-3">Filter by reason</div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant={activeRule === null ? 'default' : 'outline'} size="sm"
                onClick={() => filterByRule(null)} data-testid="failure-group-all"
              >All</Button>
              {report.failure_groups.map((g) => (
                <Button
                  key={g.rule_key}
                  variant={activeRule === g.rule_key ? 'default' : 'outline'}
                  size="sm" onClick={() => filterByRule(g.rule_key)}
                  data-testid={`failure-group-${g.rule_key}`}
                >
                  {g.label} <Badge variant="secondary" className="ml-2">{g.count}</Badge>
                </Button>
              ))}
            </div>
          </Card>
        )}

        <Card className="overflow-hidden" data-testid="validation-list-table">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8"></TableHead>
                <TableHead>Status</TableHead>
                {isFailedTab && <TableHead>Reason</TableHead>}
                <TableHead>Vendor</TableHead>
                <TableHead>Account #</TableHead>
                <TableHead className="text-right">Total Due</TableHead>
                <TableHead className="text-center">Bill</TableHead>
                <TableHead className="text-right">{isFailedTab ? 'Actions' : 'Promoted'}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow><TableCell colSpan={colCount} className="text-center text-muted-foreground py-8">
                  {isFailedTab
                    ? <>No failed validations. Click <strong>Run Validation</strong> to evaluate recent parsed bills.</>
                    : <>No passed validations yet.</>}
                </TableCell></TableRow>
              ) : rows.map((r) => (
                <Fragment key={r.id}>
                  <TableRow
                    className="cursor-pointer"
                    onClick={() => setExpanded(expanded === r.bill_id ? null : r.bill_id)}
                    data-testid={`validation-row-${r.bill_id}`}
                  >
                    <TableCell>
                      {expanded === r.bill_id
                        ? <ChevronDown className="h-4 w-4 text-muted-foreground" />
                        : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                    </TableCell>
                    <TableCell><StatusBadge status={r.overall_status} /></TableCell>
                    {isFailedTab && <TableCell><ReasonBadges reasons={r.failure_reasons} /></TableCell>}
                    <TableCell>{r.vendor_name || '\u2014'}</TableCell>
                    <TableCell>{r.account_number || '\u2014'}</TableCell>
                    <TableCell className="text-right">{fmtC(r.total_amount_due)}</TableCell>
                    <TableCell className="text-center">
                      <Button
                        variant="ghost" size="icon" className="h-8 w-8"
                        onClick={(e) => { e.stopPropagation(); openBill(r); }}
                        data-testid={`validation-bill-pdf-${r.bill_id}`}
                        title="View bill PDF"
                      >
                        <FileText className="h-4 w-4" />
                      </Button>
                    </TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      {isFailedTab ? (
                        <Button
                          size="sm" variant="outline"
                          onClick={() => reviewFix(r)}
                          data-testid={`validation-review-fix-${r.bill_id}`}
                        >
                          <Wrench className="h-3.5 w-3.5 mr-1" /> Review &amp; fix
                        </Button>
                      ) : (
                        r.promoted
                          ? <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200" variant="outline">Yes</Badge>
                          : (
                            <div className="inline-flex flex-col items-end gap-0.5" data-testid={`validation-promoted-no-${r.bill_id}`}>
                              <Badge variant="outline" className="text-muted-foreground gap-1">
                                <Info className="h-3 w-3" /> No
                              </Badge>
                              <span
                                className="text-[11px] leading-tight text-amber-600 max-w-[220px] text-right"
                                data-testid={`validation-promoted-reason-${r.bill_id}`}
                              >
                                {promotionReasonLabel(r.promotion_blocked_reason)}
                              </span>
                            </div>
                          )
                      )}
                    </TableCell>
                  </TableRow>
                  {expanded === r.bill_id && (
                    <TableRow key={`${r.id}-detail`} data-testid={`validation-detail-${r.bill_id}`}>
                      <TableCell colSpan={colCount} className="p-0">
                        <RuleDetailRow row={r} />
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              ))}
            </TableBody>
          </Table>
        </Card>

        {!loading && rows.length > 0 && (
          <div className="flex items-center justify-between text-sm text-muted-foreground" data-testid="validation-pagination">
            <span>Showing {rows.length} of {total}</span>
            {rows.length < total && (
              <Button variant="outline" size="sm" onClick={loadMore} disabled={loadingMore} data-testid="validation-load-more">
                {loadingMore ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
                Load more ({total - rows.length} left)
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Bill PDF — left drawer */}
      <Sheet open={billOpen} onOpenChange={setBillOpen}>
        <SheetContent side="left" className="w-full sm:max-w-2xl flex flex-col" data-testid="validation-bill-drawer">
          <SheetHeader>
            <SheetTitle>Bill — {billRow?.vendor_name || ''} · {billRow?.account_number || ''}</SheetTitle>
            <SheetDescription>Source PDF for the parsed bill.</SheetDescription>
          </SheetHeader>
          <div className="flex-1 mt-4 min-h-0">
            {billLoading ? (
              <div className="h-full flex items-center justify-center text-muted-foreground">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : billPdfUrl ? (
              <iframe title="bill-pdf" src={billPdfUrl} className="w-full h-full rounded-md border" data-testid="validation-bill-iframe" />
            ) : (
              <div className="h-full flex items-center justify-center text-sm text-muted-foreground">
                No PDF available for this bill.
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </MainLayout>
  );
}
